import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Loader2, RefreshCw } from "lucide-react";

import Meta from "../../components/Meta";
import api from "../../utils/axiosInstance";
import { getApiErrorMessage } from "../../utils/errorMessages";

import "./LegalPageScreen.css";

export const LEGAL_NAV_ITEMS = Object.freeze([
  {
    title: "Terms and Conditions",
    slug: "terms-and-conditions",
    path: "/terms-and-conditions",
  },
  {
    title: "Privacy Policy",
    slug: "privacy-policy",
    path: "/privacy-policy",
  },
  {
    title: "Cookie Policy",
    slug: "cookie-policy",
    path: "/cookie-policy",
  },
]);

const VALID_LEGAL_SLUGS = new Set(LEGAL_NAV_ITEMS.map((item) => item.slug));

function safeText(value = "") {
  return String(value ?? "").trim();
}

function formatDate(value = "") {
  if (!value) return "Not set";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "Not set";

  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "long",
  }).format(date);
}

function isSectionHeading(line = "") {
  const text = safeText(line);

  return (
    text.length > 0 &&
    text.length <= 80 &&
    !/[.!?)]$/.test(text) &&
    !text.includes(":")
  );
}

function openCookieSettings() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event("openCookieSettings"));
}

function LegalBody({ body = "" }) {
  const blocks = safeText(body)
    .split(/\n{2,}|\r?\n/)
    .map(safeText)
    .filter(Boolean);

  return (
    <div className="legal-page__body">
      {blocks.map((block, index) =>
        isSectionHeading(block) ? (
          <h2 key={`${block}-${index}`}>{block}</h2>
        ) : (
          <p key={`${block}-${index}`}>{block}</p>
        ),
      )}
    </div>
  );
}

export default function LegalPageScreen({ slug: slugProp = "" }) {
  const { slug: routeSlug = "" } = useParams();
  const slug = safeText(slugProp || routeSlug);

  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const currentNavItem = useMemo(
    () => LEGAL_NAV_ITEMS.find((item) => item.slug === slug) || null,
    [slug],
  );

  const loadPage = useCallback(async () => {
    if (!VALID_LEGAL_SLUGS.has(slug)) return;

    setLoading(true);
    setError("");

    try {
      const { data } = await api.get(`/legal/${encodeURIComponent(slug)}`);
      setPage(data?.page || null);
    } catch (err) {
      setError(
        getApiErrorMessage(err, {
          fallback:
            "We could not load this legal page right now. Please try again later.",
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  if (!VALID_LEGAL_SLUGS.has(slug)) {
    return <Navigate to="/terms-and-conditions" replace />;
  }

  const title = page?.title || currentNavItem?.title || "Legal";

  return (
    <main className="legal-page">
      <Meta
        title={`${title} | Academy Costumes London`}
        description={`${title} for Academy Costumes London.`}
        url={currentNavItem?.path || `/legal/${slug}`}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: title, url: currentNavItem?.path || `/legal/${slug}` },
        ]}
      />

      <section className="legal-page__shell">
        <nav className="legal-page__nav" aria-label="Legal pages">
          {LEGAL_NAV_ITEMS.map((item) => (
            <Link
              key={item.slug}
              to={item.path}
              className={item.slug === slug ? "is-active" : ""}
            >
              {item.title}
            </Link>
          ))}
        </nav>

        <article className="legal-page__article">
          <header className="legal-page__header">
            <p>Academy Costumes London</p>
            <h1>{title}</h1>
            <span>Last updated {formatDate(page?.lastUpdated)}</span>
          </header>

          {loading ? (
            <div className="legal-page__state">
              <Loader2 size={22} className="legal-page__spin" />
              <p>Loading legal page...</p>
            </div>
          ) : error ? (
            <div className="legal-page__state legal-page__state--error">
              <p>{error}</p>
              <button type="button" onClick={loadPage}>
                <RefreshCw size={15} />
                Retry
              </button>
            </div>
          ) : (
            <>
              <LegalBody body={page?.body} />
              {page?.shopCancellationInformation ? <section id="shop-cancellation">
                <h2>Shop Cancellation and Returns</h2>
                <p>{page.shopCancellationInformation}</p>
                <p>{page.shopModelCancellationForm}</p>
                <p>{page.shopReturnContact?.name}, {page.shopReturnContact?.address}. {page.shopReturnContact?.email}</p>
                <Link to="/contact">Contact Academy Costumes London</Link>
              </section> : null}

              {slug === "cookie-policy" ? (
                <section className="legal-page__cookie-action">
                  <h2>Cookie Settings</h2>
                  <p>
                    You can review, change, or withdraw your cookie preferences
                    at any time. Essential cookies are always active because
                    they are needed for core site functions such as security,
                    basket, checkout, and account access. Non-essential cookies
                    are only used where you choose to allow them.
                  </p>

                  <button type="button" onClick={openCookieSettings}>
                    Open Cookie Settings
                  </button>
                </section>
              ) : null}
            </>
          )}
        </article>
      </section>
      {/* CookieConsent is mounted globally in App.js */}
    </main>
  );
}
